/**
 * Shrinks a video before upload: H.264/AAC MP4 (plays everywhere, the mobile
 * app included), long side at most 1280px, medium quality, index at the front
 * so playback can start while it downloads.
 *
 * Runs on the browser's own encoder (WebCodecs, via mediabunny — loaded only
 * when a video is actually sent). Never fails the send: if the browser can't
 * encode, the conversion errors, or the result isn't smaller, the original
 * file comes back unchanged.
 */

const MAX_SIDE = 1280;

export const compressVideo = async (
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<File> => {
  try {
    const {
      ALL_FORMATS,
      BlobSource,
      BufferTarget,
      Conversion,
      Input,
      Mp4OutputFormat,
      Output,
      QUALITY_MEDIUM,
    } = await import("mediabunny");

    const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
    const output = new Output({
      format: new Mp4OutputFormat({ fastStart: "in-memory" }),
      target: new BufferTarget(),
    });
    const conversion = await Conversion.init({
      input,
      output,
      // Scale down only: set just the long side, the other follows the ratio.
      video: (track) => {
        const landscape = track.displayWidth >= track.displayHeight;
        const long = landscape ? track.displayWidth : track.displayHeight;
        const size = Math.min(long, MAX_SIDE);
        return {
          codec: "avc",
          quality: QUALITY_MEDIUM,
          forceTranscode: true,
          ...(landscape ? { width: size } : { height: size }),
        };
      },
      audio: { codec: "aac", quality: QUALITY_MEDIUM },
    });
    if (!conversion.isValid) return file;

    if (onProgress) conversion.onProgress = (p) => onProgress(p);
    await conversion.execute();

    const buffer = output.target.buffer;
    if (!buffer || buffer.byteLength >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, "") || "video";
    return new File([buffer], `${name}.mp4`, { type: "video/mp4" });
  } catch {
    return file;
  }
};
